import { useEffect } from "react";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
} from "@mui/material";
import { useSetAtom } from "jotai";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CloseIcon, SaveIcon } from "../../../../../../../../../../assets/icons";
import {
	FilterDTO,
	FilterOperatorDTO,
	FiltersListDTO,
	SiteFiltersPayload,
} from "../../../../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../../../../datasources/networking/apiRoutes";
import { useWriteResource } from "../../../../../../../../../../hooks/useResource";
import { SelectNodeFilter } from "./filters/SelectNodeFilter";
import { FilterText } from "./filters/TextNodeFilter";

// Filter form item
export type FilterFormItem = {
	id: string;
	label: string;
	operator: string;
	value: string | string[];
};

// Form values
export type FiltersFormValues = {
	filters: FilterFormItem[];
};

type FiltersDialogProps = {
	open: boolean;
	handleClose: VoidFunction;
	nodeID: string;
	items: FilterDTO[];
	onChangeItems: (items: FilterDTO[]) => void;
};

export const FiltersDialog = ({ open, handleClose, nodeID, items, onChangeItems }: FiltersDialogProps) => {
	const { t } = useTranslation("common");
	const form = useForm<FiltersFormValues>({
		defaultValues: { filters: [] },
		mode: "onChange",
	});

	const { control, handleSubmit } = form;

	const { fields, replace } = useFieldArray({
		control,
		name: "filters",
	});

	const { put: updateFilters, isLoading: isLoadingFilters } = useWriteResource<SiteFiltersPayload, FiltersListDTO>(
		{ route: ApiRoutes.ConfigurationFilters, path: { id: nodeID } },
		{ subjectKey: "filtering" }
	);

	// When dialog opens, initialize it with items from props
	useEffect(() => {
		if (!open) return;

		// map api filters to form filters
		const mapped: FilterFormItem[] = items.map((item) => {
			const preselectedId = item.preselectedValueId;

			const defaultOperator =
				item.operators.find((op: FilterOperatorDTO) => op.operator === "=")?.operator ??
				item.operators?.[0]?.operator ??
				"";

			// find operator that matches preselected logic if possible
			const matchedOperator =
				item.operators.find((op: FilterOperatorDTO) => op.operator === defaultOperator) ?? item.operators?.[0];

			const isMulti = matchedOperator?.supportsMultiSelect;

			// normalize value from API
			let value: string | string[] = "";

			if (item.type === "select") {
				value = isMulti
					? preselectedId
						? [String(preselectedId)]
						: []
					: preselectedId
						? String(preselectedId)
						: "";
			}

			if (item.type === "text") {
				value = item.preselectedValue ?? "";
			}

			return {
				id: item.id,
				label: item.label,
				operator: defaultOperator,
				value,
			};
		});

		// replace the form array with the mapped items
		replace(mapped);
	}, [open, items, replace]);

	// map form values to api payload
	const mapToPayload = (data: FiltersFormValues): SiteFiltersPayload => ({
		items: data.filters.map((filter) => ({
			id: filter.id,

			// convert array values to comma separated string
			// api accepts only string
			value: Array.isArray(filter.value) ? filter.value.join(",") : filter.value,
		})),
	});

	const onSubmit = async (data: FiltersFormValues) => {
		try {
			await updateFilters(mapToPayload(data));
			const updatedItems = items.map((item) => {
				const formValue = data.filters.find((f) => f.id === item.id);

				if (!formValue) return item;

				return {
					...item,
					preselectedValue: Array.isArray(formValue.value) ? formValue.value.join(",") : formValue.value,
				};
			});

			onChangeItems(updatedItems);

		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
				<DialogTitle sx={{ p: 0 }}>Filters</DialogTitle>

				<IconButton onClick={handleClose}>
					<CloseIcon />
				</IconButton>
			</Stack>

			<Divider sx={{ mx: 2 }} />

			<DialogContent sx={{ p: 2 }}>
				<Stack spacing={2}>
					{items.map((item, index) => {
						const field = fields[index];

						const watchedOperator = form.watch(`filters.${index}.operator`);

						const meta = item.operators.find((op: FilterOperatorDTO) => op.operator === watchedOperator);

						const isRequired = meta?.argsType !== "none";

						if (!field) return null;

						// Text filter
						if (item.type === "text") {
							return (
								<FilterText
									key={item.id}
									index={index}
									form={form}
									item={item}
									rules={{ required: isRequired ? t("this field is required_short") : false }}
								/>
							);
						}

						// Select filter
						if (item.type === "select") {
							return (
								<SelectNodeFilter
									key={item.id}
									index={index}
									form={form}
									item={item}
									rules={{ required: isRequired ? t("this field is required_short") : false }}
								/>
							);
						}

						return null;
					})}

					<Stack direction="row" justifyContent="flex-end">
						<Button
							disabled={isLoadingFilters}
							variant="contained"
							onClick={handleSubmit(onSubmit)}
							startIcon={
								isLoadingFilters ? (
									<CircularProgress size={18} color="inherit" />
								) : (
									<SaveIcon color="inherit" />
								)
							}
						>
							{t("save filter")}
						</Button>
					</Stack>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
